export const DEFAULT_FIRST_OPEN_AFTER_MILLIS = 24 * 60 * 1000;
export const DEFAULT_MIN_OPEN_INTERVAL_MILLIS = 30 * 60 * 1000;
export const DEFAULT_MAX_OPEN_COUNT = 2;
export const DEFAULT_FIRST_OPEN_ORDER_ALC = 2;
export const DEFAULT_FIRST_OPEN_ORDER_BF = 4;
export const ALC_TYPE = 1;
export const BF_TYPE = 2;
export const RATE_TIP_AUTO_OPEN_TRIGGERS = new Set([
  "end-call",
  "order-submitted",
  "confirm-payment",
]);

export const normalizeMenuType = (serviceType) => {
  if (serviceType === undefined || serviceType === null || serviceType === "") {
    return "ALC";
  }

  const normalized = String(serviceType).trim().toUpperCase();
  if (
    normalized === String(BF_TYPE) ||
    normalized === "BF" ||
    normalized === "BUFFET"
  ) {
    return "BF";
  }

  return "ALC";
};

export const normalizeRateTipServiceType = (serviceType) => {
  return normalizeMenuType(serviceType) === "BF" ? BF_TYPE : ALC_TYPE;
};

export const getConfigValue = (source, key) => {
  if (!source) return undefined;

  const directKeys = [
    key,
    key.toLowerCase(),
    key.toUpperCase(),
    key.replace(/_/g, "-").toLowerCase(),
    key.replace(/_/g, "-"),
  ];

  for (const directKey of directKeys) {
    if (source?.[directKey] !== undefined) {
      return source[directKey];
    }
  }

  const containers = [
    source,
    source?.params,
    source?.configs,
    source?.settings,
    source?.["lst-config"],
    source?.["lst-configs"],
    source?.["config-list"],
  ].filter(Array.isArray);

  for (const items of containers) {
    const matched = items.find((item) => {
      const itemKey = item?.["param-code"];
      return String(itemKey || "").toUpperCase() === key.toUpperCase();
    });

    if (matched) {
      return (
        matched?.value ??
        matched?.["param-value"] ??
        matched?.["config-value"] ??
        matched?.["data-value"]
      );
    }
  }

  return undefined;
};

export const getBooleanConfig = (source, key, fallback = false) => {
  const rawValue = getConfigValue(source, key);
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return fallback;
  }
  if (typeof rawValue === "boolean") return rawValue;

  const normalized = String(rawValue).trim().toLowerCase();
  return normalized === "true" || normalized === "1";
};

export const getNumberConfig = (source, key, fallback) => {
  const rawValue = getConfigValue(source, key);
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return fallback;
  }

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const parseOrderTimestamp = (submitTime) => {
  if (!submitTime) return 0;
  if (typeof submitTime === "number") return submitTime;

  const asNumber = Number(submitTime);
  if (Number.isFinite(asNumber) && asNumber > 0) {
    return asNumber;
  }

  const parsedDate = new Date(submitTime).getTime();
  return Number.isFinite(parsedDate) ? parsedDate : 0;
};

export const getFirstOpenRule = ({ paramConfig, serviceType }) => {
  const menuType = normalizeMenuType(serviceType);
  const defaultRule = {
    firstOpenAfterMillis: DEFAULT_FIRST_OPEN_AFTER_MILLIS,
    firstOpenOrderCount:
      menuType === "BF"
        ? DEFAULT_FIRST_OPEN_ORDER_BF
        : DEFAULT_FIRST_OPEN_ORDER_ALC,
  };

  const rawValue = getConfigValue(paramConfig, "FIRST_OPEN_AFTER_MINUTES");
  if (!rawValue) return defaultRule;

  if (typeof rawValue === "number") {
    return {
      ...defaultRule,
      firstOpenAfterMillis: rawValue * 60 * 1000,
    };
  }

  const normalized = String(rawValue).trim();
  if (/^\d+$/.test(normalized)) {
    return {
      ...defaultRule,
      firstOpenAfterMillis: Number(normalized) * 60 * 1000,
    };
  }

  const menuKey = menuType;
  const matchedRule = normalized
    .split(",")
    .map((item) => item.trim())
    .find((item) => item.toUpperCase().startsWith(`${menuKey}:`));

  if (!matchedRule) return defaultRule;

  const [, valuePart = ""] = matchedRule.split(":");
  const [waitMinutes, orderCount] = valuePart.split("-").map(Number);

  return {
    firstOpenAfterMillis: Number.isFinite(waitMinutes)
      ? waitMinutes * 60 * 1000
      : defaultRule.firstOpenAfterMillis,
    firstOpenOrderCount: Number.isFinite(orderCount)
      ? orderCount
      : defaultRule.firstOpenOrderCount,
  };
};

export const shouldAllowRateTipAutoOpen = ({
  trigger,
  qrCode,
  pathname,
  isFollowUp,
  paramConfig,
  serviceType,
  autoOpenState,
  sessions,
  ratingCompleted,
  paymentFlowBlocked,
  now = Date.now(),
  followUpTriggerPaths,
}) => {
  const currentState = autoOpenState || {};
  const orderSessions = sessions || [];
  const firstOpenRule = getFirstOpenRule({ paramConfig, serviceType });
  const isAutoOpenEnabled =
    getBooleanConfig(paramConfig, "AUTO_OPEN_RATETIP", false) ||
    getBooleanConfig(paramConfig, "PARAM_AUTO_OPEN_RATETIP", false);
  const maxOpenCount = getNumberConfig(
    paramConfig,
    "MAX_OPEN_COUNT",
    DEFAULT_MAX_OPEN_COUNT,
  );
  const minOpenIntervalMinutes = getNumberConfig(
    paramConfig,
    "MIN_OPEN_INTERVAL_MINUTES",
    DEFAULT_MIN_OPEN_INTERVAL_MILLIS / (60 * 1000),
  );
  const minOpenIntervalMillis = minOpenIntervalMinutes * 60 * 1000;
  const firstOrderTime =
    currentState.firstOrderAt ||
    parseOrderTimestamp(orderSessions?.[0]?.["submit-time"]) ||
    0;

  const debug = {
    isFollowUp,
    trigger,
    pathname,
    qrCode,
    serviceType,
    isAutoOpenEnabled,
    paymentFlowBlocked,
    ratingCompleted,
    openCount: currentState.openCount || 0,
    maxOpenCount,
    minOpenIntervalMillis,
    lastOpenAt: currentState.lastOpenAt || 0,
    elapsedFromLastOpen: currentState.lastOpenAt
      ? now - currentState.lastOpenAt
      : null,
    sessionCount: orderSessions.length,
    firstOrderTime,
    elapsedFromFirstOrder: firstOrderTime ? now - firstOrderTime : null,
    firstOpenRule,
    hasFollowUpPath: followUpTriggerPaths?.has(pathname),
    autoOpenState: currentState,
  };

  if (!RATE_TIP_AUTO_OPEN_TRIGGERS.has(trigger)) {
    return { allowed: false, reason: "unsupported-trigger", debug };
  }

  if (!qrCode) {
    return { allowed: false, reason: "no-qrcode", debug };
  }
  if (!isAutoOpenEnabled) {
    return { allowed: false, reason: "disabled", debug };
  }
  if (ratingCompleted) {
    return { allowed: false, reason: "completed", debug };
  }
  if ((currentState.openCount || 0) >= maxOpenCount) {
    return { allowed: false, reason: "max-open-count", debug };
  }
  if (
    (currentState.openCount || 0) > 0 &&
    now - (currentState.lastOpenAt || 0) < minOpenIntervalMillis
  ) {
    return { allowed: false, reason: "min-interval", debug };
  }
  return { allowed: true, reason: "supported-trigger", debug };
};
